/**
 * The words the recognizer is told to expect before a rep speaks.
 *
 * A general model has never heard of a customer's drugs, so a brand comes back
 * as the nearest ordinary word — and the mis-heard string is what lands in the
 * note. The detector's catalog grounding corrects which record gets attached,
 * not the text the rep reads back. Biasing the recognizer fixes it at source.
 *
 * Two sources, in this order:
 *  1. What an administrator typed in `Companion.DictationPhrases` — the same
 *     config the Companion app primes its recognizer from, so one vocabulary
 *     serves both apps.
 *  2. The rep's own products, which need no curation to be useful.
 *
 * What the organisation named comes first: where the cap bites, it is the
 * curated vocabulary that survives.
 *
 * Pure and Angular-free: callers pass the config value and the offline brands.
 */

/** Minimal brand shape these hints need; the real {@link Brand} class satisfies it. */
export interface PhraseBrand {
  name?: string;
}

/**
 * Azure accepts large phrase lists, but every phrase dilutes the bias and the
 * list is sent on connect. Well above a territory's catalog, and far above the
 * hundred the Companion app is held to by the phone platforms.
 */
export const MAX_SPEECH_PHRASES = 250;

/** Titles a recognizer already knows, and which cost a phrase for nothing. */
const HONORIFICS = new Set([
  'dr', 'dr.', 'prof', 'prof.', 'mr', 'mr.', 'mrs', 'mrs.', 'ms', 'ms.', 'mx', 'mx.',
]);

/**
 * What an administrator typed, as a list. Commas or lines, because a field an
 * administrator fills in is a field they will use both in — matching how the
 * Companion app reads the same config.
 */
/**
 * The vocabulary that applies to this rep: a business unit's own list where one is
 * set, the organisation's otherwise. A business unit that has configured its own
 * terms means them instead of the wider list, not alongside it — an oncology unit
 * should not be biased toward respiratory molecules it never discusses.
 */
export function pickConfiguredPhrases(
  businessUnit: string | null | undefined,
  organisation: string | null | undefined,
): string | null {
  const bu = (businessUnit ?? '').trim();
  if (bu) {
    return bu;
  }
  const org = (organisation ?? '').trim();
  return org || null;
}

export function parseConfiguredPhrases(said: string | null | undefined): string[] {
  return (said ?? '')
    .split(/[,\n]/)
    .map(phrase => phrase.trim())
    .filter(phrase => phrase.length > 0);
}

/** `Dr. Amara Okafor` is two words a model has not heard and one it has. */
function withoutTitle(phrase: string): string {
  const words = phrase.trim().split(/\s+/).filter(Boolean);
  while (words.length && HONORIFICS.has(words[0].toLowerCase())) {
    words.shift();
  }
  return words.join(' ').trim();
}

export interface SpeechPhraseSources {
  /** Raw `Companion.DictationPhrases` value, as the administrator typed it. */
  configured?: string | null;
  /** The rep's own offline brands. */
  brands?: readonly PhraseBrand[] | null;
  limit?: number;
}

/**
 * The list a session is primed with: the configured vocabulary, then product
 * names, de-duplicated case-insensitively and capped.
 *
 * Key messages are deliberately excluded — they are whole sentences reps
 * paraphrase heavily, so they bias poorly and would crowd out the brand names
 * that need the help. Matching those stays with the detector.
 */
export function buildSpeechPhrases({
  configured,
  brands,
  limit = MAX_SPEECH_PHRASES,
}: SpeechPhraseSources): string[] {
  const said: string[] = [];
  const seen = new Set<string>();
  const candidates = [
    ...parseConfiguredPhrases(configured),
    ...(brands ?? []).map(brand => brand?.name ?? ''),
  ];
  for (const candidate of candidates) {
    const kept = withoutTitle(candidate ?? '');
    if (!kept) continue;
    const key = kept.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    said.push(kept);
    if (said.length >= limit) break;
  }
  return said;
}
